import React, { useMemo } from "react";
import { Text } from "@geist-ui/core";
import { Badge, Button, Flex, Text as RadixText } from "@radix-ui/themes";
import { TrashIcon } from "@radix-ui/react-icons";

import { useSimulatorTracking } from "@/routes/simulator/useSimulatorTracking";
import { useSimulator } from "../../context/SimulatorWrapper";
import { Instruction } from "@/v2/entities/instruction/Instruction.model";

import styles from "./styles.module.scss";

interface InstructionsInstructionsProps {
  topicId: number;
  instructions: Instruction[];
  trackingData: any;
  getInstructionStatus: (instruction: Instruction) => {
    isNewInstruction: boolean;
    isEditedInstruction: boolean;
  };
}

export const InstructionsInstructions: React.FC<
  InstructionsInstructionsProps
> = ({ instructions, trackingData, getInstructionStatus }) => {
  const { deleteInstruction } = useSimulator();
  const { trackInstructionAdded } = useSimulatorTracking();

  const instructionsToDisplay = useMemo(() => {
    return instructions.filter(
      (i) => i.content.toLowerCase() !== "think carefully before answering"
    );
  }, [instructions]);

  return (
    <div className={styles.instructionsSection}>
      <Flex justify="between" align="center" mb="2">
        <Flex align="center" gap="2">
          <Text style={{ fontSize: "14px", fontWeight: 500 }}>
            Instructions
          </Text>
          <Badge
            size="1"
            variant="solid"
            style={{
              backgroundColor: "var(--gray-4)",
              color: "var(--gray-12)",
            }}
          >
            {instructionsToDisplay?.length ?? 0}
          </Badge>
        </Flex>
      </Flex>

      {instructionsToDisplay?.length === 0 ? (
        <div className={styles.emptyStateContainer}>
          <Text small style={{ color: "var(--gray-9)" }}>
            No instructions added. Instructions will be collected as you
            interact with the simulator.
          </Text>
        </div>
      ) : (
        <div className={styles.instructionsTable}>
          {instructionsToDisplay?.map((instruction, index) => {
            const { isNewInstruction, isEditedInstruction } =
              getInstructionStatus(instruction);

            return (
              <div key={index} className={styles.instructionRow}>
                <Flex gap="2" align="start" justify="between">
                  <Flex gap="2" align="start">
                    <RadixText
                      size="1"
                      style={{
                        color: "var(--gray-9)",
                        minWidth: "16px",
                        paddingTop: "2px",
                      }}
                    >
                      {index + 1}.
                    </RadixText>
                    {(isNewInstruction || isEditedInstruction) && (
                      <Badge
                        size="1"
                        variant="solid"
                        style={{
                          backgroundColor: "var(--blue-3)",
                          color: "var(--blue-11)",
                        }}
                      >
                        New
                      </Badge>
                    )}
                    <div className={styles.instructionContent}>
                      {instruction.content}
                    </div>
                  </Flex>
                  <Button
                    variant="ghost"
                    color="gray"
                    size="1"
                    onClick={async () => {
                      trackInstructionAdded(instruction.content);
                      await deleteInstruction(instruction.id);
                    }}
                  >
                    <TrashIcon style={{ width: "14px", height: "14px" }} />
                  </Button>
                </Flex>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
